/**
 * Contador grande de empresas cadastradas, por status (pedido do dono).
 *
 * Decisoes nao obvias:
 *  1. O numero grande e o TOTAL cadastrado; a barra empilhada e a legenda
 *     dizem quantas estao ativas (rendendo), em implantacao (fila de
 *     receita) e pausadas. Cada fatia tem o numero escrito ao lado: a cor e
 *     reforco, nunca o unico canal.
 *  2. "Novas no periodo" respeita a janela do cabecalho; "com alerta aberto"
 *     e retrato de agora. As duas frases dizem de qual base vieram.
 *  3. Nenhum `primary` aqui: a figura principal da pagina e a comissao ao
 *     lado. "Nova empresa" e secundario e "Ver empresas" e ghost.
 */

import { Badge, Icon, LinkButton, Panel } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { CompanyStatus } from '../../../types';
import { COMPANY_STATUS_BAR, COMPANY_STATUS_LABEL, COMPANY_STATUS_ORDER } from '../vocabulario';

export function ContadorDeEmpresas({
  statusCounts,
  novas,
  comAlerta,
  periodo,
  className = '',
}: {
  readonly statusCounts: Readonly<Record<CompanyStatus, number>>;
  /** Cadastradas dentro da janela do cabecalho. */
  readonly novas: number;
  /** Empresas distintas com ao menos um alerta aberto agora. */
  readonly comAlerta: number;
  readonly periodo: string;
  readonly className?: string;
}): JSX.Element {
  const total = COMPANY_STATUS_ORDER.reduce((acc, s) => acc + statusCounts[s], 0);
  const fatias = COMPANY_STATUS_ORDER.filter((s) => statusCounts[s] > 0);

  return (
    <Panel className={`flex flex-col gap-4 px-5 py-5 ${className}`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-medium text-ink-muted">
          <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-lg bg-brand/10 text-brand">
            <Icon name="empresa" size="sm" />
          </span>
          Empresas cadastradas
        </h3>
        {novas > 0 ? (
          <Badge tone="good" icon="mais">
            {formatCount(novas)} {novas === 1 ? 'nova' : 'novas'}
          </Badge>
        ) : null}
      </div>

      <div className="flex items-end justify-between gap-4">
        <p className="text-5xl font-semibold tracking-tight text-ink sm:text-6xl">{formatCount(total)}</p>
        <p className="mb-1 text-right text-xs text-ink-subtle">{formatCount(statusCounts.ativa)} rendendo agora</p>
      </div>

      {total === 0 ? (
        <p className="text-sm text-ink-muted">Nenhuma empresa cadastrada ainda. A primeira implantação começa pelo cadastro.</p>
      ) : (
        <>
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-ink/10" role="img" aria-label={fatias.map((s) => `${formatCount(statusCounts[s])} ${COMPANY_STATUS_LABEL[s].toLowerCase()}`).join(', ')}>
            {fatias.map((s) => (
              <span key={s} className={`h-full ${COMPANY_STATUS_BAR[s]}`} style={{ width: `${(statusCounts[s] * 100) / total}%` }} />
            ))}
          </div>
          <ul className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            {COMPANY_STATUS_ORDER.map((s) => (
              <li key={s} className="flex items-center gap-1.5 text-ink-muted">
                <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${COMPANY_STATUS_BAR[s]}`} />
                <span className="font-semibold tabular-nums text-ink">{formatCount(statusCounts[s])}</span>
                {COMPANY_STATUS_LABEL[s].toLowerCase()}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="text-xs text-ink-subtle">
        {novas === 0 ? 'Nenhuma cadastrada' : `${formatCount(novas)} ${novas === 1 ? 'cadastrada' : 'cadastradas'}`} em {periodo.toLowerCase()} ·{' '}
        {comAlerta === 0 ? 'nenhuma com alerta aberto' : `${formatCount(comAlerta)} com alerta aberto agora`}
      </p>

      <div className="mt-auto flex flex-wrap gap-2">
        <LinkButton to="/admin/empresas/nova" variant="secondary" size="sm" icon="mais">
          Nova empresa
        </LinkButton>
        <LinkButton to="/admin/empresas" variant="ghost" size="sm" icon="empresa">
          Ver empresas
        </LinkButton>
      </div>
    </Panel>
  );
}
